import dayjs from "dayjs";
import {
  AI_SPREADSHEET_CELL_KIND,
  AI_SPREADSHEET_MANUAL_ROW_OUTCOME,
  AI_SPREADSHEET_OVERRIDE_OUTCOME,
  type IAiSpreadsheetCellOverride,
  type IAiSpreadsheetManualRow,
  type IAiSpreadsheetSheet,
} from "./types";

export type OverrideKind = "applied" | "needs_review" | "dropped";

export const formatPersistentEditValue = (
  value: string | number | boolean | null | undefined,
): string => {
  if (value === null || value === undefined || value === "") return "–";

  // Keep integer-like identifiers exact while stripping binary-float noise
  // from decimal values (15.437000000000001 → 15.437).
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Number.isInteger(value)) return String(value);

    return String(Number.parseFloat(value.toPrecision(12)));
  }

  return String(value);
};

const formatEditAttribution = (
  fullName: string | null | undefined,
  at: string,
): string => {
  const timestamp = dayjs(at).format("MMM D, h:mm A");
  return fullName ? `${fullName} · ${timestamp}` : timestamp;
};

export const formatPersistentEditMetadata = (
  override: Pick<IAiSpreadsheetCellOverride, "edited_at" | "edited_by">,
): string =>
  formatEditAttribution(override.edited_by?.full_name, override.edited_at);

// The audit lines for a user-added row: who put it there, and — only once
// someone has — who last changed one of its cells. A manual-row cell edit
// rewrites the row's seed instead of creating an override, so such an edit never
// appears as an entry of its own in the panel; this card is where it surfaces.
//
// Empty when the API hasn't sent `added_at` (an older payload mid-deploy), so the
// card falls back to how it looked before rather than dating the row from nothing.
// A row whose author was never recorded is dated but not credited — crediting it
// to whoever edited it last would be worse than saying nothing.
export const getManualRowAudit = (
  row: Pick<
    IAiSpreadsheetManualRow,
    "added_at" | "added_by" | "edited_at" | "edited_by"
  >,
): string[] => {
  if (!row.added_at) return [];

  const line = (
    verb: string,
    at: string,
    fullName: string | null | undefined,
  ) =>
    fullName
      ? `${verb} by ${formatEditAttribution(fullName, at)}`
      : `${verb} ${formatEditAttribution(null, at)}`;

  const lines = [line("Added", row.added_at, row.added_by?.full_name)];
  if (row.edited_at) {
    lines.push(line("Last edited", row.edited_at, row.edited_by?.full_name));
  }

  return lines;
};

const MANUAL_ROW_LABEL_VALUES = 3;

const getManualRowLabel = (row: IAiSpreadsheetManualRow): string => {
  const values = Object.keys(row.cells)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => {
      const cell = row.cells[key];
      return cell?.kind === AI_SPREADSHEET_CELL_KIND.formula
        ? cell.raw_input
        : cell?.value;
    })
    .filter((value) => value != null && String(value).trim() !== "")
    .slice(0, MANUAL_ROW_LABEL_VALUES)
    .map((value) => String(value).trim());

  return values.length > 0 ? values.join(" · ") : "Empty row";
};

export interface ManualRowDisplay {
  label: string;
  note: string | null;
  wasMoved: boolean;
}

export const getManualRowDisplay = (
  row: IAiSpreadsheetManualRow,
): ManualRowDisplay => {
  const wasMoved =
    row.outcome === AI_SPREADSHEET_MANUAL_ROW_OUTCOME.needsReview;
  const isOrphaned = !row.cell && row.outcome !== null;
  const note = isOrphaned
    ? `${row.sheet_name} wasn't part of the latest run, so this row isn't in the workbook. It comes back if that sheet does.`
    : !row.cell
      ? `Saved to ${row.sheet_name}. It appears in the sheet the next time payroll runs.`
      : wasMoved
        ? "The spot this row sat in wasn't on the latest run, so it's at the end of the sheet now."
        : null;

  return {
    label: getManualRowLabel(row),
    note,
    wasMoved,
  };
};

// Applied edits whose numbers moved (or whose key was ambiguous) this run.
export const isOverrideNeedsReview = (override: IAiSpreadsheetCellOverride) =>
  override.outcome !== AI_SPREADSHEET_OVERRIDE_OUTCOME.unmapped &&
  (override.stale ||
    override.outcome === AI_SPREADSHEET_OVERRIDE_OUTCOME.needsReview);

export const isOverrideDropped = (override: IAiSpreadsheetCellOverride) =>
  override.outcome === AI_SPREADSHEET_OVERRIDE_OUTCOME.unmapped;

// Edits that need a decision (flagged for review or dropped) — the badge count.
export const manualEditsReviewCount = (
  overrides: IAiSpreadsheetCellOverride[],
): number =>
  overrides.filter(
    (override) =>
      isOverrideNeedsReview(override) || isOverrideDropped(override),
  ).length;

export const getOverrideKind = (
  override: IAiSpreadsheetCellOverride,
): OverrideKind => {
  if (isOverrideDropped(override)) return "dropped";
  if (isOverrideNeedsReview(override)) return "needs_review";
  return "applied";
};

export const groupPersistentEdits = (overrides: IAiSpreadsheetCellOverride[]) =>
  overrides.reduce<{
    applied: IAiSpreadsheetCellOverride[];
    needsReview: IAiSpreadsheetCellOverride[];
    dropped: IAiSpreadsheetCellOverride[];
  }>(
    (groups, override) => {
      const kind = getOverrideKind(override);
      if (kind === "applied") groups.applied.push(override);
      if (kind === "needs_review") groups.needsReview.push(override);
      if (kind === "dropped") groups.dropped.push(override);
      return groups;
    },
    { applied: [], needsReview: [], dropped: [] },
  );

export const canResolvePersistentEdit = (
  override: IAiSpreadsheetCellOverride,
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[],
): boolean => {
  if (override.cell) {
    return sheets.some(
      (sheet) => sheet.id === override.cell?.sheet_id && sheet.editable,
    );
  }

  // The API only returns cell-less overrides to full-workbook viewers. Its
  // mutation contract additionally requires edit access to at least one sheet.
  return sheets.some((sheet) => sheet.editable);
};

export const isPersistentEditCandidateEditable = (
  sheetId: string,
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[],
): boolean => sheets.some((sheet) => sheet.id === sheetId && sheet.editable);
